// #region Imports

/* Vue */
import Vue from 'vue';

/* uuid */
import {v4} from 'uuid';

/* Telestream */
import { SubtractTimecodes, ToFrames } from '@user-interface/timecode-utilities';
import {
  CLEAR_CLIPS,
  CLEAR_MARK_IN,
  CLEAR_MARK_OUT,
  COPY_CLIPLIST_PATH,
  EXPORT_CLIPS,
  FETCH_CLIPLIST_PLUGIN_SCHEMAS,
  IMPORT_CLIPS,
  LOAD_CLIPS,
  LOAD_VIDEO,
  LOG_CLIP,
  MARK_IN,
  MARK_OUT,
  MOVE_CLIP_DOWN,
  MOVE_CLIP_UP,
  REMOVE_CLIP,
  SELECT_CLIPS_OUTPUT,
  SUBMIT_CLIPS,
  RESET_VIDEO_STATE,
  UPDATE_CLIP,
  UPDATE_CLIP_THUMBNAIL
} from '@/utils/action-types';
import {
  SET_CLIP_LIST_OUTPUTS,
  SET_CLIP_LIST_OUTPUT_CONFIGURATION,
  SET_SELECTED_CLIP_LIST_OUTPUT,
  SET_CLIPS,
  SET_STAGED_CLIP,
  SET_CLIPS_ELEMENT
} from '@/utils/mutation-types';
import {
  storeClipList,
  restoreClipList,
  storeClipListOutputConfiguration,
  restoreClipListOutputConfiguration,
  storeClipListOutputSelection,
  restoreClipListOutputSelection,
  removeObsoleteCliplistOutputConfigurations
} from './utilities/persistence.utils';
import { ConnectionService } from '@/services/connection.service';
import { thumbnailFromElement } from './utilities/thumbnails.utils';
import { generateRandomClipColor, compressClipList, openClipLists } from './utilities/clips.utils';
import { LEVELS, sendEphemeralSnack } from './utilities/snacks.utils';
import { copyUsingTextArea } from './utilities/clipboard.utils';

// #endregion Imports

// #region Constants

const EXPORT_FILE_NAME    = 'GlimClipList.json';

// #endregion Constants

export const clips = {

  // #region State

  state: {

    outputs:        [],
    selectedOutput: restoreClipListOutputSelection(),

    stagedClip: {
      id:        undefined,       // Uniquely identifies this clip.
      thumbnail: undefined,
      path:      undefined,
      in:        undefined,       // Frame index where the clip starts.
      out:       undefined,       // Frame index where the clip ends.
      framerate: undefined,       // The framerate of the clip.
      dropframe: undefined,       // Whether or not the clip is dropframe.
      offset:    undefined,       // The first timecode of the media the clip is from.
      notes:     undefined
    },
    clips:   restoreClipList(),
    element: undefined

  },

  // #endregion State

  // #region Mutations

  mutations: {

    [SET_CLIP_LIST_OUTPUTS](state, outputs) {
      state.outputs = outputs;
    },

    [SET_STAGED_CLIP](state, clip) {
      state.stagedClip = clip;
    },

    [SET_CLIPS](state, clips) {
      state.clips = clips;
    },

    [SET_CLIPS_ELEMENT](state, element) {
      state.element = element;
    },

    [SET_CLIP_LIST_OUTPUT_CONFIGURATION](state, {name, configuration}) {
      const output = state.outputs.find(o => o.name === name);
      if (!output) {
        return;
      }

      output.model = configuration;
    },

    [SET_SELECTED_CLIP_LIST_OUTPUT](state, output) {
      state.selectedOutput = output;
    }

  },

  // #endregion Mutations

  // #region Actions

  actions: {

    // #region Plugins

    async [FETCH_CLIPLIST_PLUGIN_SCHEMAS](context) {
      const connection = context.rootState.plugins.connection;
      if (!connection) {
        return;
      }

      const serializedSchemas = await connection.invoke('GetClipListPluginSchemas');
      const schemas = serializedSchemas.map((s) => JSON.parse(s));

      schemas.forEach(s => {
        const persistedConfiguration = restoreClipListOutputConfiguration(s.name);
        if (persistedConfiguration) {
          s.model = persistedConfiguration;
        }
      });

      context.commit(SET_CLIP_LIST_OUTPUTS, schemas);

      removeObsoleteCliplistOutputConfigurations(schemas.map(s => s.name));
    },

    // #endregion Plugins

    // #region Staging

    [RESET_VIDEO_STATE](context) {
      const clip = {
        ...context.state.stagedClip,
        path:      undefined,
        in:        undefined,
        out:       undefined,
        thumbnail: undefined,
        framerate: undefined,
        dropframe: undefined,
        offset:    undefined
      };

      context.commit(SET_STAGED_CLIP, clip);
      context.commit(SET_CLIP_LIST_OUTPUTS, []);
    },

    [MARK_IN](context) {
      const clip = {
        ...context.state.stagedClip,
        path:      context.rootState.videoPath,
        thumbnail: thumbnailFromElement(context.rootState.video.element),
        in:        context.rootState.video.currentFrame,
        framerate: context.rootState.video.framerate,
        dropframe: context.rootState.video.firstTimecode.includes(';'),
        offset:    context.rootState.video.firstTimecode
      };

      context.commit(SET_STAGED_CLIP, clip);
    },

    [CLEAR_MARK_IN](context) {
      const clip = {
        ...context.state.stagedClip,
        thumbnail: undefined,
        in:        undefined
      };

      context.commit(SET_STAGED_CLIP, clip);
    },

    [MARK_OUT](context) {
      const clip = {
        ...context.state.stagedClip,
        out: context.rootState.video.currentFrame
      };

      context.commit(SET_STAGED_CLIP, clip);
    },

    [CLEAR_MARK_OUT](context) {
      const clip = {
        ...context.state.stagedClip,
        out: undefined
      };

      context.commit(SET_STAGED_CLIP, clip);
    },

    // #endregion Staging

    // #region Clips

    [LOG_CLIP](context) {
      const inFrame = context.state.stagedClip.in || 0;
      const outFrame = context.state.stagedClip.out || 
                        ToFrames(
                          SubtractTimecodes(
                            context.rootState.video.lastTimecode, 
                            context.rootState.video.firstTimecode
                          )
                        );

      if (inFrame > outFrame) {
        return;
      }

      const previousClip = context.state.clips[context.state.clips.length - 1];
      let previousColor = undefined;
      if (previousClip) {
        previousColor = previousClip.color;
      }

      const clips = [
        ...context.state.clips,
        {
          ...context.state.stagedClip,

          path:      context.state.stagedClip.path || context.rootState.videoPath,
          thumbnail: context.state.stagedClip.thumbnail || thumbnailFromElement(context.rootState.video.element),
          in:        inFrame,
          out:       outFrame,
          framerate: context.state.stagedClip.framerate || context.rootState.video.framerate,
          dropframe: context.state.stagedClip.dropframe || context.rootState.video.firstTimecode.includes(';'),
          offset:    context.state.stagedClip.offset || context.rootState.video.firstTimecode,

          // Properties that are generated for every new clip:
          id:    v4(),
          color: generateRandomClipColor(previousColor)
        }
      ];

      context.commit(SET_CLIPS, clips);
      storeClipList(clips);

      if (context.state.element) {
        const cliplistElement = context.state.element.querySelector('.clip-list');
        if (cliplistElement) {
          Vue.nextTick(() => cliplistElement.scrollTop = cliplistElement.scrollHeight);
        }
      }
    },

    [UPDATE_CLIP](context, updatedClip) {
      const updatedClips = [...context.state.clips];
      const index = updatedClips.findIndex(c => c.id === updatedClip.id);
      updatedClips.splice(index, 1, updatedClip);

      context.commit(SET_CLIPS, updatedClips);
      storeClipList(updatedClips);
    },

    [REMOVE_CLIP](context, id) {
      const clips = context.state.clips.filter(c => c.id !== id);

      context.commit(SET_CLIPS, clips);
      storeClipList(clips);
    },

    [CLEAR_CLIPS](context) {
      context.commit(SET_CLIPS, []);
      storeClipList([]);
    },

    [MOVE_CLIP_DOWN](context, id) {
      const updatedClips = [...context.state.clips];
      const index = updatedClips.findIndex(c => c.id === id);

      const itemsRemoved = updatedClips.splice(index, 1);
      updatedClips.splice(index + 1, 0, itemsRemoved[0]);

      context.commit(SET_CLIPS, updatedClips);
      storeClipList(updatedClips);
    },

    [MOVE_CLIP_UP](context, id) {
      const updatedClips = [...context.state.clips];
      const index = updatedClips.findIndex(c => c.id === id);

      const itemsRemoved = updatedClips.splice(index, 1);
      updatedClips.splice(index - 1, 0, itemsRemoved[0]);

      context.commit(SET_CLIPS, updatedClips);
      storeClipList(updatedClips);
    },

    [UPDATE_CLIP_THUMBNAIL](context, id) {
      const updatedClip = {
        ...context.state.clips.find(c => c.id === id),
        thumbnail: thumbnailFromElement(context.rootState.video.element)
      };

      context.dispatch(UPDATE_CLIP, updatedClip);
    },

    [SELECT_CLIPS_OUTPUT](context, output) {
      storeClipListOutputSelection(output);
      context.commit(SET_SELECTED_CLIP_LIST_OUTPUT, output);
    },

    async [SUBMIT_CLIPS](context, output) {
      await context.rootState.plugins.connection.invoke(
        'SubmitClipList',
        output.name,
        JSON.stringify(output.configuration),
        ConnectionService.getUrl(''),
        compressClipList(context.state.clips)
      );

      storeClipListOutputConfiguration(output.name, output.configuration);
      context.commit(SET_CLIP_LIST_OUTPUT_CONFIGURATION, output);
    },

    // #endregion Clips

    // #region Load / Import / Export

    async [LOAD_CLIPS](context, paths) {
      try {
        const clips = await openClipLists(paths);
        clips.forEach(c => c.id = v4());

        const allClips = [...context.state.clips, ...clips];
        context.commit(SET_CLIPS, allClips);
        storeClipList(allClips);
      }

      catch (e) {
        sendEphemeralSnack(context, 'error', `Unable to load clip list: ${e}`);
      }
    },

    [EXPORT_CLIPS](context) {
      const content = compressClipList(context.state.clips);
      const serializedContent = JSON.stringify(content, null, 2);

      const link = document.createElement('a');
      link.download = EXPORT_FILE_NAME;
      link.href = `data:text/plain;charset=utf-8,${encodeURIComponent(serializedContent)}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    [IMPORT_CLIPS](context, file) {
      const reader = new FileReader();
      let previousColor = undefined;
      reader.addEventListener(
        'load',
        event => {
          const deserializedClips = JSON.parse(event.target.result);
          deserializedClips.forEach(c => {
            c.id = v4();
            c.color = generateRandomClipColor(previousColor);

            previousColor = c.color;
          });

          if (deserializedClips.length === 0) {
            return;
          }

          const allClips = [...context.state.clips, ...deserializedClips];
          context.commit(SET_CLIPS, allClips);
          storeClipList(allClips);

          if (!context.rootState.videoPath) {
            context.dispatch(LOAD_VIDEO, { videoPath: deserializedClips[0].path });
          }
        }
      );

      reader.readAsText(file);
    },

    [COPY_CLIPLIST_PATH](context, action) {
      const cliplink = `${window.location.origin}/?cliplist=${encodeURIComponent(action.file.path)}`;
      if (navigator.clipboard) {
        navigator.clipboard.writeText(cliplink);
      } else {
        copyUsingTextArea(cliplink);
      }

      sendEphemeralSnack(context, LEVELS.INFO, 'Clip list link copied to clipboard.');
    }

    // #endregion Import / Export

  }

  // #endregion Actions

}